import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { FinancialSection, FinancialField, FinancialSectionTemplate } from '../models/financial-section.interface';

/**
 * Servicio para gestiâ”œâ”‚n de secciones financieras
 * Siguiendo principios de Clean Code - Single Responsibility
 */
@Injectable({
  providedIn: 'root'
})
export class FinancialSectionService {
  private sectionsSubject = new BehaviorSubject<FinancialSection[]>([]);
  public sections$ = this.sectionsSubject.asObservable();

  // Templates predefinidos para diferentes tipos de secciones
  private readonly sectionTemplates: FinancialSectionTemplate[] = [
    {
      id: 'rentabilidad-template',
      name: 'Análisis de rentabilidad',
      type: 'income',
      color: '#f09502',
      fields: [
        { label: 'Plazo objetivo (en meses)', type: 'number', required: true, placeholder: 'meses' },
        { label: 'Ingreso Neto por venta', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Ingresos brutos por venta', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Plusvalía', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Comisión inmobiliaria venta', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Ingresos aportados por el promotor', type: 'percentage', required: true, placeholder: '%0.00' },
        { label: 'Ingresos aportados por los inversores', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Beneficio del proyecto', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Beneficio Promotora por éxito del proyecto', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Rentabilidad total inversor', type: 'percentage', required: true, placeholder: '%0.00' },
        { label: 'Rentabilidad anualizada inversor (TIR)', type: 'percentage', required: true, placeholder: '%0.00' }
      ]
    },
    {
      id: 'inversion-template',
      name: 'Inversión Proyecto',
      type: 'expense',
      color: '#f09502',
      fields: [
        { label: 'Precio de compra', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Comisión inmobiliaria', type: 'percentage', required: true, placeholder: '%0.00' },
        { label: 'Impuestos fiscales (ITP,..)', type: 'percentage', required: true, placeholder: '%0.00' },
        { label: 'Notaría, gestoría, registro', type: 'percentage', required: true, placeholder: '%0.00' },
        { label: 'Constructora', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Licencias y costes legales de obra', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Infografías', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Aparejador externo', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Muebles', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Fotógrafo', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Otros gastos (luz, agua, ibi.,alarmas)', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Gastos imprevistos', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Otros gastos adecuación', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Desarrollo y Ejecución del Proyecto', type: 'currency', required: true, placeholder: '€0.00' },
        { label: 'Gestión Domoblock', type: 'percentage', required: true, placeholder: '%0.00' }
      ]
    }
  ];

  constructor() {
    this.initializeDefaultSections();
  }

  getSectionsSnapshot(): FinancialSection[] {
    return this.cloneSections(this.sectionsSubject.value);
  }

  setSections(sections: FinancialSection[]): void {
    this.sectionsSubject.next(this.cloneSections(sections));
  }

  resetToDefaultSections(): void {
    this.sectionsSubject.next(this.buildDefaultSections());
  }

  getDefaultSectionsSnapshot(): FinancialSection[] {
    return this.cloneSections(this.buildDefaultSections());
  }

  /**
   * Inicializa secciones por defecto
   */
  private initializeDefaultSections(): void {
    this.sectionsSubject.next(this.buildDefaultSections());
  }

  private buildDefaultSections(): FinancialSection[] {
    return [
      this.createRentabilidadSection(),
      this.createInversionSection()
    ];
  }

  private cloneSections(sections: FinancialSection[]): FinancialSection[] {
    return sections.map(section => ({
      ...section,
      fields: section.fields.map(field => ({ ...field })),
      subsections: (section.subsections || []).map(subsection => ({
        ...subsection,
        fields: subsection.fields.map(field => ({ ...field }))
      }))
    }));
  }

  /**
   * Crea la secciâ”œâ”‚n de rentabilidad
   */
  private createRentabilidadSection(): FinancialSection {
    return {
      id: 'rentabilidad',
      title: 'Análisis de rentabilidad',
      type: 'income',
      color: '#f09502',
      fields: [
        { id: 'plazoObjetivo', label: 'Plazo objetivo (en meses)', type: 'number', value: null, required: true, placeholder: 'meses' },
        { id: 'ingresoNetoVenta', label: 'Ingreso Neto por venta', type: 'currency', value: null, required: true, placeholder: '€0.00' },
        { id: 'ingresoBrutoVenta', label: 'Ingresos brutos por venta', type: 'currency', value: null, required: true, placeholder: '€0.00' },
        { id: 'plusvalia', label: 'Plusvalía', type: 'currency', value: null, required: true, placeholder: '€0.00' },
        { id: 'comisionInmobiliariaVenta', label: 'Comisión inmobiliaria venta', type: 'currency', value: null, required: true, placeholder: '€0.00' },
        { id: 'ingresosAportadosPromotor', label: 'Ingresos aportados por el promotor', labelPercentage: 5, type: 'currency', value: null, required: true, placeholder: '€0.00' },
        { id: 'ingresosAportadosInversores', label: 'Ingresos aportados por los inversores', type: 'currency', value: null, required: true, placeholder: '€0.00' },
        { id: 'beneficioProyecto', label: 'Beneficio del proyecto', type: 'currency', value: null, required: true, placeholder: '€0.00' },
        { id: 'beneficioPromotora', label: 'Beneficio Promotora por éxito del proyecto', type: 'currency', value: null, required: true, placeholder: '€0.00' },
        { id: 'rentabilidadTotalInversor', label: 'Rentabilidad total inversor', type: 'percentage', value: null, required: true, placeholder: '%0.00' },
        { id: 'rentabilidadAnualizadaInversor', label: 'Rentabilidad anualizada inversor (TIR)', type: 'percentage', value: null, required: true, placeholder: '%0.00' }
      ],
      subsections: [],
      order: 0,
      isCollapsed: false
    };
  }

  /**
   * Crea la sección de inversión con subsecciones
   */
  private createInversionSection(): FinancialSection {
    return {
      id: 'inversion',
      title: 'Inversión Proyecto',
      type: 'expense',
      color: '#f09502',
      fields: [],
      subsections: [
        {
          id: 'acquisitionCosts',
          title: 'Gastos adquisición',
          isFixed: true,
          fields: [
            { id: 'precioCompra', label: 'Precio de compra', type: 'currency', value: null, required: true, placeholder: '€0.00' },
            { id: 'comisionInmobiliaria', label: 'Comisión inmobiliaria', labelPercentage: 1, type: 'currency', value: null, required: true, placeholder: '€0.00' },
            { id: 'impuestosFiscales', label: 'Impuestos fiscales (ITP,..)', labelPercentage: 8, type: 'currency', value: null, required: true, placeholder: '€0.00' },
            { id: 'notariaGestoriaRegistro', label: 'Notaría, gestoría, registro', labelPercentage: 1, type: 'currency', value: null, required: true, placeholder: '€0.00' }
          ]
        },
        {
          id: 'constructionCosts',
          title: 'Obra y otros gastos',
          isFixed: true,
          fields: [
            { id: 'constructora', label: 'Constructora', labelPercentage: 5, type: 'currency', value: null, required: true, placeholder: '€0.00' },
            { id: 'licenciasCostesLegalesObra', label: 'Licencias y costes legales de obra', type: 'currency', value: null, required: true, placeholder: '€0.00' },
            { id: 'infografias', label: 'Infografías', type: 'currency', value: null, required: true, placeholder: '€0.00' },
            { id: 'aparejadorExterno', label: 'Aparejador externo', type: 'currency', value: null, required: true, placeholder: '€0.00' },
            { id: 'muebles', label: 'Muebles', type: 'currency', value: null, required: true, placeholder: '€0.00' },
            { id: 'fotografo', label: 'Fotógrafo', type: 'currency', value: null, required: true, placeholder: '€0.00' },
            { id: 'otrosGastos', label: 'Otros gastos (luz, agua, ibi.,alarmas)', type: 'currency', value: null, required: true, placeholder: '€0.00' },
            { id: 'gastosImprevistos', label: 'Gastos imprevistos', type: 'currency', value: null, required: true, placeholder: '€0.00' },
            { id: 'otrosGastosAdecuacion', label: 'Otros gastos adecuación', type: 'currency', value: null, required: true, placeholder: '€0.00' }
          ]
        },
        {
          id: 'subtotalInversion',
          title: 'Subtotal Inversión',
          isFixed: true,
          fields: []
        },
        {
          id: 'gestionesDomoblock',
          title: 'Gestiones',
          isFixed: true,
          fields: [
            { id: 'desarrolloEjecucionProyecto', label: 'Desarrollo y Ejecución del Proyecto', type: 'currency', value: null, required: true, placeholder: '€0.00' },
            { id: 'gestionDomoblock', label: 'Gestión Domoblock', labelPercentage: 1.78, type: 'currency', value: null, required: true, placeholder: '€0.00' }
          ]
        },
        {
          id: 'costesTotales',
          title: 'Costes totales de la inversión',
          isFixed: true,
          fields: []
        }
      ],
      order: 1,
      isCollapsed: false
    };
  }

  /**
   * Obtiene todas las secciones
   */
  getSections(): FinancialSection[] {
    return this.sectionsSubject.value;
  }

  /**
   * Obtiene templates disponibles
   */
  getTemplates(): FinancialSectionTemplate[] {
    return [...this.sectionTemplates];
  }

  /**
   * Aâ”œâ–’ade una nueva secciâ”œâ”‚n desde template
   */
  addSectionFromTemplate(templateId: string): void {
    const template = this.sectionTemplates.find(t => t.id === templateId);
    if (!template) return;

    const sections = this.getSections();
    const newSection = this.createSectionFromTemplate(template, sections.length);
    
    this.sectionsSubject.next([...sections, newSection]);
  }

  /**
   * Aâ”œâ–’ade una secciâ”œâ”‚n personalizada
   */
  addCustomSection(title: string, type: FinancialSection['type']): void {
    const sections = this.getSections();
    const newSection: FinancialSection = {
      id: this.generateId(),
      title,
      type,
      fields: [],
      subsections: [],
      order: sections.length,
      isCollapsed: false,
      color: this.getColorForType(type)
    };

    this.sectionsSubject.next([...sections, newSection]);
  }

  /**
   * Elimina una secciâ”œâ”‚n
   */
  deleteSection(sectionId: string): void {
    const sections = this.getSections().filter(s => s.id !== sectionId);
    this.reorderSections(sections);
    this.sectionsSubject.next(sections);
  }

  /**
   * Actualiza una secciâ”œâ”‚n
   */
  updateSection(sectionId: string, updates: Partial<FinancialSection>): void {
    const sections = this.getSections();
    const index = sections.findIndex(s => s.id === sectionId);
    
    if (index !== -1) {
      sections[index] = { ...sections[index], ...updates };
      this.sectionsSubject.next([...sections]);
    }
  }

  /**
   * Reordena secciones (drag & drop)
   */
  reorderSections(newOrder: FinancialSection[]): void {
    const reorderedSections = newOrder.map((section, index) => ({
      ...section,
      order: index
    }));
    this.sectionsSubject.next(reorderedSections);
  }

  /**
   * Aâ”œâ–’ade un campo a una secciâ”œâ”‚n
   */
  addFieldToSection(sectionId: string, field: Omit<FinancialField, 'id'>): void {
    const sections = this.getSections();
    const sectionIndex = sections.findIndex(s => s.id === sectionId);
    
    if (sectionIndex !== -1) {
      const newField: FinancialField = {
        ...field,
        id: this.generateId()
      };
      
      sections[sectionIndex].fields.push(newField);
      this.sectionsSubject.next([...sections]);
    }
  }

  /**
   * Aâ”œâ–’ade un campo a una subsecciâ”œâ”‚n especâ”œÂ¡fica
   */
  addFieldToSubsection(sectionId: string, subsectionId: string, field: Omit<FinancialField, 'id'>): void {
    const sections = this.getSections();
    const sectionIndex = sections.findIndex(s => s.id === sectionId);
    
    if (sectionIndex !== -1) {
      const section = sections[sectionIndex];
      const subsectionIndex = section.subsections.findIndex(sub => sub.id === subsectionId);
      
      if (subsectionIndex !== -1) {
        const newField: FinancialField = {
          ...field,
          id: this.generateId()
        };
        
        section.subsections[subsectionIndex].fields.push(newField);
        this.sectionsSubject.next([...sections]);
      }
    }
  }

  /**
   * Actualiza un campo
   */
  updateField(sectionId: string, fieldId: string, updates: Partial<FinancialField>): void {
    const sections = this.getSections();
    const sectionIndex = sections.findIndex(s => s.id === sectionId);
    
    if (sectionIndex !== -1) {
      const section = sections[sectionIndex];
      
      // Buscar y actualizar en campos directos de la secciâ”œâ”‚n
      const fieldIndex = section.fields.findIndex(f => f.id === fieldId);
      if (fieldIndex !== -1) {
        section.fields[fieldIndex] = {
          ...section.fields[fieldIndex],
          ...updates
        };
        this.sectionsSubject.next([...sections]);
        return;
      }
      
      // Buscar y actualizar en campos de subsecciones
      section.subsections.forEach(subsection => {
        const subsectionFieldIndex = subsection.fields.findIndex(f => f.id === fieldId);
        if (subsectionFieldIndex !== -1) {
          subsection.fields[subsectionFieldIndex] = {
            ...subsection.fields[subsectionFieldIndex],
            ...updates
          };
          this.sectionsSubject.next([...sections]);
        }
      });
    }
  }

  /**
   * Elimina un campo
   */
  deleteField(sectionId: string, fieldId: string): void {
    const sections = this.getSections();
    const sectionIndex = sections.findIndex(s => s.id === sectionId);
    
    if (sectionIndex !== -1) {
      const section = sections[sectionIndex];
      
      // Buscar y eliminar en campos directos de la secciâ”œâ”‚n
      section.fields = section.fields.filter(f => f.id !== fieldId);
      
      // Buscar y eliminar en campos de subsecciones
      section.subsections.forEach(subsection => {
        subsection.fields = subsection.fields.filter(f => f.id !== fieldId);
      });
      
      this.sectionsSubject.next([...sections]);
    }
  }

  /**
   * Calcula totales automâ”œÃ­ticamente
   */
  calculateTotals(): { totalIncome: number; totalExpense: number; netProfit: number } {
    const sections = this.getSections();
    let totalIncome = 0;
    let totalExpense = 0;

    sections.forEach(section => {
      if (section.type === 'income') {
        totalIncome += this.calculateSectionTotal(section);
      } else if (section.type === 'expense') {
        totalExpense += this.calculateSectionTotal(section);
      }
    });

    return {
      totalIncome,
      totalExpense,
      netProfit: totalIncome - totalExpense
    };
  }

  /**
   * Calcula el total de una secciâ”œâ”‚n
   */
  private calculateSectionTotal(section: FinancialSection): number {
    let total = section.fields.reduce((acc, field) => {
      return acc + (field.type !== 'percentage' ? (field.value || 0) : 0);
    }, 0);
    
    // Add amounts from all subsections if they exist
    if (section.subsections && section.subsections.length > 0) {
      section.subsections.forEach(subsection => {
        total += subsection.fields.reduce((acc, field) => {
          return acc + (field.type !== 'percentage' ? (field.value || 0) : 0);
        }, 0);
      });
    }
    
    return total;
  }

  /**
   * Crea una secciâ”œâ”‚n desde template
   */
  private createSectionFromTemplate(template: FinancialSectionTemplate, order: number): FinancialSection {
    return {
      id: this.generateId(),
      title: template.name,
      type: template.type,
      fields: template.fields.map(field => ({
        ...field,
        id: this.generateId(),
        value: null
      })),
      subsections: [],
      order,
      isCollapsed: false,
      color: template.color
    };
  }

  /**
   * Obtiene color para tipo de secciâ”œâ”‚n
   */
  private getColorForType(type: FinancialSection['type']): string {
    const colors = {
      income: '#28a745',
      expense: '#dc3545',
      calculation: '#17a2b8',
      summary: '#6f42c1'
    };
    return colors[type] || '#6c757d';
  }

  /**
   * Reordena campos dentro de una secciâ”œâ”‚n
   */
  reorderFields(sectionId: string, previousIndex: number, currentIndex: number): void {
    const sections = [...this.sectionsSubject.value];
    const sectionIndex = sections.findIndex(s => s.id === sectionId);
    
    if (sectionIndex !== -1) {
      const section = sections[sectionIndex];
      const fields = [...section.fields];
      
      // Mover el campo
      const [movedField] = fields.splice(previousIndex, 1);
      fields.splice(currentIndex, 0, movedField);
      
      // Actualizar la secciâ”œâ”‚n
      sections[sectionIndex] = {
        ...section,
        fields
      };
      
      this.sectionsSubject.next(sections);
    }
  }

  /**
   * Genera ID â”œâ•‘nico
   */
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
}


